﻿// ---------------------------------------------------------------------------
// flux_engine.ts  —  Complex-Potential Field Solver
//
// Maxwell-style field simulation on a circuit graph.  Each node holds a
// complex voltage V = a + bi.  Each strand carries complex current
// I = (V_from - V_to) / Z.  Loops apply complex phase rotation to
// currents (inductance).  Sources and co-sources are Dirichlet BCs.
//
// No particles — just a field evolving via Gauss-Seidel relaxation at 30Hz.
// Lifecycle: capacitor discharge between source/co-source pairs.
// ---------------------------------------------------------------------------

import type {
  StrandGraph, StrandId, NodeId, LoopId, Loop, Strand, StrandNode,
} from './strand_graph.js';

// ── Complex arithmetic helpers ───────────────────────────────────────────────

interface C { re: number; im: number; }

const ZERO: C = { re: 0, im: 0 };

function cAdd(a: C, b: C): C { return { re: a.re + b.re, im: a.im + b.im }; }
function cSub(a: C, b: C): C { return { re: a.re - b.re, im: a.im - b.im }; }
function cMul(a: C, b: C): C {
  return { re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re };
}
function cDiv(a: C, b: C): C {
  const d = b.re * b.re + b.im * b.im;
  if (d < 1e-12) return ZERO;
  return { re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d };
}
function cAbs(a: C): number { return Math.sqrt(a.re * a.re + a.im * a.im); }
function cScale(a: C, s: number): C { return { re: a.re * s, im: a.im * s }; }
function cExp(theta: number): C { return { re: Math.cos(theta), im: Math.sin(theta) }; }

// ── Wand lifecycle ────────────────────────────────────────────────────────────

export type WandPhase = 'idle' | 'running' | 'resolved' | 'fizzling' | 'dead';

// ── Source / Co-Source pair ───────────────────────────────────────────────────

export interface SourcePair {
  /** Node clamped to +V0 (pen-down point). */
  sourceNodeId: number;
  /** Node clamped to -V0 (pen-up point). */
  coSourceNodeId: number;
  /** Initial potential magnitude. */
  V0: number;
  /** Whether co-source is still live (pen still drawing this stroke). */
  live: boolean;
}

// ── Flux Engine State ────────────────────────────────────────────────────────

export interface FluxState {
  time: number;
  phase: WandPhase;
  settled: boolean;
  fizzleStart: number;
  _lastActiveTick: number;
  /** Source/co-source pairs — one per committed stroke. */
  pairs: SourcePair[];
  /** Set of clamped node IDs (sources + co-sources). */
  clampedNodes: Set<number>;
  /** Peak current magnitude seen so far (for normalisation). */
  peakCurrent: number;
  /** Faraday-induced current injected into self-loop strands (strand id → I). */
  _loopInducedI: Map<number, C>;
}

// Legacy re-exports for backward compat with consumers
export type Mote = never;
export type MoteId = never;

// ── Constants ────────────────────────────────────────────────────────────────

/** Base potential for source/co-source (arbitrary units). */
const V0_BASE = 1.0;

/** Characteristic length for strand impedance (px). Real part of Z. */
const LAMBDA = 200;

/** Node capacitance — how quickly free nodes respond to current. */
const NODE_CAP = 0.06;

/** Endpoint leak rate — open endpoints bleed potential toward zero. */
const ENDPOINT_LEAK = 0.02;

/** Loop inductance base factor (rad per unit area ratio). */
const LOOP_INDUCTANCE_K = 0.3;

/** Reference area for inductance normalisation (px^2). */
const AREA_REF = 10000;

/** Mutual induction range multiplier (× loop radius). */
const MUTUAL_RANGE = 2.0;

/** Mutual induction coupling constant. */
const MUTUAL_K = 0.15;

/** Fizzle fade duration (ms). */
const FIZZLE_FADE_MS = 1000;

/** Discharge threshold — when |V_src - V_cosrc| < this, pair is resolved. */
const DISCHARGE_THRESHOLD = 0.05;

/** Death threshold — when max |V| across all nodes < this, system is dead. */
const DEATH_THRESHOLD = 0.02;

/** Min runtime before death check (ms). */
const MIN_RUNTIME_MS = 800;

/** Flow animation speed factor (parametric units per ms per unit current). */
const FLOW_SPEED_K = 0.0015;

/** Phosphor stamp from current flow. */
const PHOSPHOR_FROM_CURRENT = 0.4;

/** Phosphor decay half-life (ms). */
const PHOSPHOR_HALF_LIFE = 2000;
const PHOSPHOR_DECAY_K = Math.LN2 / PHOSPHOR_HALF_LIFE;

/** Shimmer boost from loop induction. */
const SHIMMER_FROM_INDUCTION = 0.8;

/** Gauss-Seidel iterations per tick (more = faster convergence). */
const GS_ITERATIONS = 16;

/** Source oscillation frequency (Hz). */
const SOURCE_OSC_HZ = 2.5;

/** Source oscillation amplitude (fraction of V0). Applied to Re(V). */
const SOURCE_OSC_AMP = 1.0;

/** Random noise amplitude on source (fraction of V0). */
const SOURCE_NOISE_AMP = 0.12;

// ── Factory ──────────────────────────────────────────────────────────────────

export function createFluxState(): FluxState {
  return {
    time: 0,
    phase: 'idle',
    settled: true,
    fizzleStart: 0,
    _lastActiveTick: 0,
    pairs: [],
    clampedNodes: new Set(),
    peakCurrent: 0,
    _loopInducedI: new Map(),
  };
}

// ── Strand impedance (complex) ───────────────────────────────────────────────

/** Compute complex impedance for a strand, considering loop membership.
 *  Real part = resistance (travel length).  Imaginary = reactance from loops. */
function strandImpedance(g: StrandGraph, strand: Strand): C {
  const R = strand.arcLength / LAMBDA + 0.05; // small floor to avoid zero-div
  let X = 0;
  // Add reactance from any loops this strand belongs to
  for (const loop of g.loops) {
    if (loop.active && loop.strands.includes(strand.id)) {
      const areaRatio = loop.area / AREA_REF;
      X += LOOP_INDUCTANCE_K * loop.winding * areaRatio;
    }
  }
  return { re: R, im: X };
}

// ── Gauss-Seidel field relaxation ────────────────────────────────────────────

/** SOR under-relaxation factor (0 < ω ≤ 2). */
const GS_OMEGA = 0.75;

/**
 * One iteration of Gauss-Seidel relaxation using admittance-weighted averaging.
 * V_target = Σ(V_j × Y_j) / Σ(Y_j)  where Y = 1/Z.
 * Complex Y naturally handles phase rotation from loop inductance.
 */
function relaxField(g: StrandGraph, fs: FluxState): void {
  for (const [nodeId, node] of g.nodes) {
    if (fs.clampedNodes.has(nodeId as number)) continue;

    // Admittance-weighted average of neighbour voltages
    let sumVY: C = { re: 0, im: 0 };
    let sumY: C = { re: 0, im: 0 };

    for (const sid of node.strands) {
      const strand = g.strands.get(sid);
      if (!strand) continue;

      const isFrom = strand.fromNode === nodeId;
      const otherNodeId = isFrom ? strand.toNode : strand.fromNode;

      // Skip self-loop strands (fromNode === toNode) — they contribute
      // no voltage gradient and only dilute the admittance sum.
      if (otherNodeId === nodeId) continue;

      const otherNode = g.nodes.get(otherNodeId);
      if (!otherNode) continue;

      const Z = strandImpedance(g, strand);
      const Y = cDiv({ re: 1, im: 0 }, Z); // admittance = 1/Z

      sumVY = cAdd(sumVY, cMul(otherNode.voltage, Y));
      sumY = cAdd(sumY, Y);
    }

    if (cAbs(sumY) < 1e-10) continue;

    const Vtarget = cDiv(sumVY, sumY);
    node.voltage = {
      re: node.voltage.re + GS_OMEGA * (Vtarget.re - node.voltage.re),
      im: node.voltage.im + GS_OMEGA * (Vtarget.im - node.voltage.im),
    };

    // Endpoint leak — open terminals bleed toward zero
    if (node.kind === 'endpoint' && !fs.clampedNodes.has(nodeId as number)) {
      node.voltage = cScale(node.voltage, 1 - ENDPOINT_LEAK);
    }
  }
}

// ── Update strand currents and visuals ───────────────────────────────────────

function updateStrandCurrents(g: StrandGraph, fs: FluxState, dt: number): void {
  const phosphorDecay = Math.exp(-PHOSPHOR_DECAY_K * dt);

  // Collect induced current for self-loop strands from driveLoopInduction
  const loopInducedI = fs._loopInducedI;

  for (const [sid, strand] of g.strands) {
    const fromNode = g.nodes.get(strand.fromNode);
    const toNode = g.nodes.get(strand.toNode);
    if (!fromNode || !toNode) continue;

    let I: C;
    if (strand.fromNode === strand.toNode) {
      // Self-loop strand — Ohm's law gives 0 since both ends are the same node.
      // Use Faraday-induced current instead.
      I = loopInducedI.get(sid as number) ?? ZERO;
    } else {
      const Z = strandImpedance(g, strand);
      const Vdiff = cSub(fromNode.voltage, toNode.voltage);
      I = cDiv(Vdiff, Z);
    }

    strand.current = I;
    strand.flux = I.re;

    const absI = cAbs(I);
    if (absI > fs.peakCurrent) fs.peakCurrent = absI;

    // Advance flow animation
    strand.flowPhase = (strand.flowPhase + absI * FLOW_SPEED_K * dt) % 1;

    // Stamp phosphor from current flow
    const phosphorAdd = Math.min(absI * PHOSPHOR_FROM_CURRENT, 1);
    const ph = strand.phosphor;
    for (let i = 0; i < ph.length; i++) {
      ph[i] = ph[i] * phosphorDecay + phosphorAdd * (1 - phosphorDecay);
    }
  }
}

// ── Loop induction + shimmer ─────────────────────────────────────────────────

function updateLoopInduction(g: StrandGraph, dt: number): void {
  const decayK = 1 - Math.exp(-dt * 0.002);

  for (const loop of g.loops) {
    if (!loop.active) continue;

    // Accumulate induction from current flowing through loop strands
    let totalCurrent = 0;
    for (const sid of loop.strands) {
      const strand = g.strands.get(sid);
      if (strand) totalCurrent += cAbs(strand.current);
    }

    // Induction builds from current, decays naturally
    loop.induction += totalCurrent * 0.3 * dt * 0.001;
    loop.induction *= (1 - decayK * 0.3);

    // Shimmer from induction (strong enough to be clearly visible)
    loop.shimmer = Math.min(1, loop.induction * SHIMMER_FROM_INDUCTION + totalCurrent * 0.15);
  }
}

// ── Mutual induction between overlapping loops ───────────────────────────────

function applyMutualInduction(g: StrandGraph, fs: FluxState): void {
  const loops = g.loops.filter(l => l.active);
  for (let i = 0; i < loops.length; i++) {
    for (let j = i + 1; j < loops.length; j++) {
      const a = loops[i], b = loops[j];
      const dx = a.center.x - b.center.x;
      const dy = a.center.y - b.center.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const range = (a.radius + b.radius) * MUTUAL_RANGE;
      if (dist > range) continue;

      // Coupling strength decays with distance
      const coupling = MUTUAL_K * (1 - dist / range);
      const areaProduct = (a.area / AREA_REF) * (b.area / AREA_REF);

      // Shared strands get a phase boost on their current
      for (const sid of a.strands) {
        if (b.strands.includes(sid)) {
          const strand = g.strands.get(sid);
          if (!strand) continue;
          const rotation = cExp(coupling * areaProduct * a.winding * b.winding);
          strand.current = cMul(strand.current, rotation);
        }
      }

      // Cross-induction: each loop's field influences the other's nodes
      const inductionTransfer = coupling * areaProduct * 0.1;
      a.induction += b.induction * inductionTransfer;
      b.induction += a.induction * inductionTransfer;
    }
  }
}

// ── Clamp source / co-source potentials ──────────────────────────────────────

function clampSources(g: StrandGraph, fs: FluxState): void {
  const t = fs.time * 0.001; // seconds
  for (const pair of fs.pairs) {
    const srcNode = g.nodes.get(pair.sourceNodeId as NodeId);
    const coNode = g.nodes.get(pair.coSourceNodeId as NodeId);

    // AC source: Re(V) oscillates at SOURCE_OSC_HZ, amplitude = V0.
    // Co-source = ground (0). Creates a true oscillating circuit.
    const osc = Math.sin(t * SOURCE_OSC_HZ * Math.PI * 2) * SOURCE_OSC_AMP;
    const noise = (Math.random() - 0.5) * 2 * SOURCE_NOISE_AMP;

    if (srcNode) {
      srcNode.voltage = { re: pair.V0 * (osc + noise), im: 0 };
    }
    if (coNode) {
      // Ground reference — always zero
      coNode.voltage = { re: 0, im: 0 };
    }
  }
}

// ── Discharge tracking ───────────────────────────────────────────────────────

function updateDischarge(g: StrandGraph, fs: FluxState, dt: number): void {
  for (const pair of fs.pairs) {
    const srcNode = g.nodes.get(pair.sourceNodeId as NodeId);
    if (!srcNode) continue;

    // Discharge rate depends on current drawn from the pair
    let totalDrain = 0;
    for (const sid of srcNode.strands) {
      const strand = g.strands.get(sid);
      if (strand) totalDrain += cAbs(strand.current);
    }

    // Live pairs discharge slower (pen still drawing = still charging)
    const rate = pair.live ? 0.0001 : 0.0003;
    const drainRate = totalDrain * rate * dt;
    // Constant background discharge so V0 always reaches 0
    const bgRate = pair.live ? 0.000005 : 0.00004;
    pair.V0 = Math.max(0, pair.V0 - drainRate - bgRate * dt);
  }
}

// ── Standing trance detection (compat) ───────────────────────────────────────

export interface StandingTrance {
  cycle: StrandId[];
  loops: LoopId[];
  flux: number;
  period: number;
}

export function detectStandingTrances(fs: FluxState): StandingTrance[] {
  // Field solver doesn't have mote history — trances are detected from
  // sustained oscillation in loop induction (future enhancement)
  return [];
}

// ── Flux Signature (backward-compat snapshot) ────────────────────────────────

export interface FluxSignature {
  endpoints: Map<number, number>;
  loops: { id: LoopId; winding: 1 | -1; scale: string; flux: number; induction: number }[];
  trances: StandingTrance[];
  aliveMotes: number;
  settled: boolean;
  intensity: number;
  coherence: number;
  phase: WandPhase;
  /** Max |V| across all nodes (field strength). */
  fieldStrength: number;
  /** Max |I| across all strands (current intensity). */
  currentPeak: number;
}

export function readFluxSignature(g: StrandGraph, fs: FluxState): FluxSignature {
  const endpoints = new Map<number, number>();
  let maxV = 0;
  for (const [, n] of g.nodes) {
    const v = cAbs(n.voltage);
    if (v > maxV) maxV = v;
    if (n.kind === 'endpoint') {
      endpoints.set(n.id as number, n.voltage.re);
    }
  }

  const loops = g.loops.filter(l => l.active).map(l => ({
    id: l.id, winding: l.winding, scale: l.scale,
    flux: l.area * l.winding, induction: l.induction,
  }));

  let maxI = 0, totalI = 0;
  for (const [, s] of g.strands) {
    const absI = cAbs(s.current);
    if (absI > maxI) maxI = absI;
    totalI += absI;
  }

  return {
    endpoints, loops,
    trances: [],
    aliveMotes: 0,  // compat
    settled: fs.settled,
    intensity: totalI,
    coherence: maxV > 0.01 ? 1 : 0,  // compat
    phase: fs.phase,
    fieldStrength: maxV,
    currentPeak: maxI,
  };
}

// ── Faraday induction drive for loops ─────────────────────────────────────────

/**
 * For each active loop, find the nearest oscillating source and inject an
 * induced EMF proportional to dV/dt of that source.  This drives current
 * through self-loop strands (where both endpoints are the same node, making
 * Ohm's law give zero) and through multi-strand loops near a changing field.
 *
 * Physics: Faraday's law — a changing external field induces EMF in a loop.
 * EMF = −L × dΦ/dt.  We approximate dΦ/dt as dV_source/dt and scale by
 * loop area and proximity.
 */
function driveLoopInduction(g: StrandGraph, fs: FluxState): void {
  fs._loopInducedI.clear();

  if (fs.pairs.length === 0) return;

  const t = fs.time * 0.001; // seconds
  const omega = SOURCE_OSC_HZ * Math.PI * 2;

  for (const loop of g.loops) {
    if (!loop.active) continue;

    // Find the source pair with a node closest to the loop center
    let bestPair: SourcePair | null = null;
    let minDist = Infinity;
    for (const pair of fs.pairs) {
      const srcNode = g.nodes.get(pair.sourceNodeId as NodeId);
      if (!srcNode) continue;
      const dx = srcNode.x - loop.center.x;
      const dy = srcNode.y - loop.center.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDist) { minDist = dist; bestPair = pair; }
    }
    if (!bestPair) continue;

    // dV/dt of the source: V = V0 × sin(ωt) → dV/dt = V0 × ω × cos(ωt)
    const dVdt = bestPair.V0 * omega * Math.cos(t * omega);

    // Scale by loop area (larger loops capture more flux)
    const areaFactor = Math.min(loop.area / AREA_REF, 3.0) + 0.15;

    // Proximity factor: closer loops couple more strongly
    const proxRange = Math.max(loop.radius * 4, 150);
    const proxFactor = Math.max(0, 1 - minDist / proxRange);
    if (proxFactor < 0.01) continue;

    // Induced EMF distributed across loop strands
    const emf = dVdt * LOOP_INDUCTANCE_K * areaFactor * proxFactor;

    for (const sid of loop.strands) {
      const strand = g.strands.get(sid);
      if (!strand) continue;

      const Z = strandImpedance(g, strand);
      // I_induced = EMF / Z  (complex division handles phase rotation)
      const I = cDiv({ re: emf, im: 0 }, Z);

      // Store for self-loop strands; for normal strands, add a voltage offset
      if (strand.fromNode === strand.toNode) {
        // Self-loop: directly inject induced current
        fs._loopInducedI.set(sid as number, I);
      } else {
        // Multi-strand loop: nudge the free node's voltage by the EMF
        const fromNode = g.nodes.get(strand.fromNode);
        const toNode = g.nodes.get(strand.toNode);
        const emfPerNode = emf * 0.3 / Math.max(loop.strands.length, 1);
        if (fromNode && !fs.clampedNodes.has(strand.fromNode as number)) {
          fromNode.voltage.re += emfPerNode;
        }
        if (toNode && !fs.clampedNodes.has(strand.toNode as number)) {
          toNode.voltage.re += emfPerNode;
        }
      }
    }
  }
}

// ── Main tick ────────────────────────────────────────────────────────────────

export function tickFlux(g: StrandGraph, fs: FluxState, dt: number): FluxSignature {
  if (fs.phase === 'dead' || fs.phase === 'idle') {
    return readFluxSignature(g, fs);
  }

  fs.time += dt;

  // Clamp source/co-source BCs
  clampSources(g, fs);

  // Gauss-Seidel relaxation (multiple iterations per tick for convergence)
  for (let i = 0; i < GS_ITERATIONS; i++) {
    relaxField(g, fs);
  }

  // Decay peakCurrent so brightness normalises to recent activity
  fs.peakCurrent *= Math.exp(-0.003 * dt);

  // Faraday induction: drive current through loops from changing source field
  driveLoopInduction(g, fs);

  // Compute strand currents + update visuals
  updateStrandCurrents(g, fs, dt);

  // Loop induction + shimmer
  updateLoopInduction(g, dt);

  // Mutual induction between overlapping loops
  applyMutualInduction(g, fs);

  // Source discharge
  if (fs.phase === 'running') {
    updateDischarge(g, fs, dt);
  }

  // Lifecycle checks
  let maxV = 0;
  for (const [, n] of g.nodes) {
    const v = cAbs(n.voltage);
    if (v > maxV) maxV = v;
  }

  const hasDynamics = maxV > DEATH_THRESHOLD;
  if (hasDynamics) fs._lastActiveTick = fs.time;

  if (fs.phase === 'running') {
    // Check if all source/co-source pairs have discharged
    const allDischarged = fs.pairs.length > 0 && fs.pairs.every(p => {
      if (p.live) return false;
      return p.V0 < DISCHARGE_THRESHOLD;
    });

    if (allDischarged) {
      // Release clamps — let the field evolve freely
      fs.clampedNodes.clear();
      fs.phase = 'fizzling';
      fs.fizzleStart = fs.time;
    }

    // Death from total field collapse
    if (fs.time > MIN_RUNTIME_MS && !hasDynamics) {
      fs.phase = 'fizzling';
      fs.fizzleStart = fs.time;
    }
  }

  // Fizzle complete → dead
  if (fs.phase === 'fizzling' && fs.time - fs.fizzleStart > FIZZLE_FADE_MS) {
    fs.phase = 'dead';
  }

  fs.settled = !hasDynamics;

  return readFluxSignature(g, fs);
}

// ── Control ──────────────────────────────────────────────────────────────────

export function activateAllLoops(g: StrandGraph, fs: FluxState): void {
  for (const loop of g.loops) { loop.active = true; }
  fs.settled = false;
}

export function deactivateAllLoops(g: StrandGraph, fs: FluxState): void {
  for (const loop of g.loops) { loop.active = false; }
}

export function clearMotes(fs: FluxState): void {
  // No motes in field solver — clear field state instead
  fs.peakCurrent = 0;
}

export function resetFlux(fs: FluxState): void {
  fs.time = 0; fs.phase = 'idle'; fs.settled = true;
  fs.fizzleStart = 0; fs._lastActiveTick = 0;
  fs.pairs = []; fs.clampedNodes = new Set(); fs.peakCurrent = 0;
  fs._loopInducedI.clear();
}

export function activateLoop(g: StrandGraph, fs: FluxState, loop: Loop): void {
  loop.active = true;
  fs.settled = false;
}

// ── Wand lifecycle ───────────────────────────────────────────────────────────

export function beginWandCast(fs: FluxState): void {
  fs.time = 0; fs.phase = 'running'; fs.settled = false;
  fs.fizzleStart = 0; fs._lastActiveTick = 0;
  fs.pairs = []; fs.clampedNodes = new Set(); fs.peakCurrent = 0;
}

/**
 * Register a source node (pen-down point) and co-source (pen-up point).
 * Both are clamped as Dirichlet BCs, forming an implicit capacitor.
 */
export function addWandSource(fs: FluxState, sourceNodeId: number, coSourceNodeId?: number): void {
  // Avoid duplicates
  if (fs.pairs.some(p => p.sourceNodeId === sourceNodeId)) return;

  const pair: SourcePair = {
    sourceNodeId,
    coSourceNodeId: coSourceNodeId ?? sourceNodeId,
    V0: V0_BASE,
    live: coSourceNodeId === undefined,
  };
  fs.pairs.push(pair);
  fs.clampedNodes.add(sourceNodeId);
  if (coSourceNodeId !== undefined) {
    fs.clampedNodes.add(coSourceNodeId);
  }
}

/**
 * Lock the co-source for the most recent pair (pen-up).
 */
export function addWandSink(fs: FluxState, coSourceNodeId: number): void {
  // Find the most recent live pair and lock its co-source
  for (let i = fs.pairs.length - 1; i >= 0; i--) {
    if (fs.pairs[i].live) {
      // Remove old co-source from clamped set
      fs.clampedNodes.delete(fs.pairs[i].coSourceNodeId);
      fs.pairs[i].coSourceNodeId = coSourceNodeId;
      fs.pairs[i].live = false;
      fs.clampedNodes.add(coSourceNodeId);
      break;
    }
  }
}

/**
 * Update the co-source node for the current live pair.
 * Called each pen-move during drawing so the co-source follows the pen tip.
 */
export function updateLiveCoSource(fs: FluxState, coSourceNodeId: number): void {
  for (let i = fs.pairs.length - 1; i >= 0; i--) {
    if (fs.pairs[i].live) {
      // Move co-source clamp to new node
      fs.clampedNodes.delete(fs.pairs[i].coSourceNodeId);
      fs.pairs[i].coSourceNodeId = coSourceNodeId;
      fs.clampedNodes.add(coSourceNodeId);
      break;
    }
  }
}

export function resolveWand(fs: FluxState): void {
  fs.phase = 'resolved';
}
